import React, { useState } from 'react'
import { FunnelIcon } from '@heroicons/react/24/outline'
import WorkersByGroupChart from 'components/charts/WorkersByGroupChart'
import WorkersInBlockChart from 'components/charts/WorkersInBlockChart'
import TableComponent from 'components/common/TableComponent'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useWorkdaySummary } from 'hooks/useWorkdaySummary'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { parseDate } from 'utils'
import { createDefaultDateRange, getDateRangeSummary } from 'utils/dateRangeFilters'

const FarmActivitySummaryTable = ({ farmActivityData }) => {
  const tableData = {
    columns: [
      { key: 'blockName', label: 'Block Name' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'cropName', label: 'Crop' },
      { key: 'varietyName', label: 'Variety' },
      { key: 'numberOfPlants', label: 'Total' },
      { key: 'workerCount', label: 'Workers Number' },
      { key: 'firstScan', label: 'First Scan', cell: (value) => parseDate(value) },
      { key: 'lastScan', label: 'Last Scan', cell: (value) => parseDate(value) }
    ],
    rows: farmActivityData
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const LastActivitiesBoardTable = ({ lastActivitiesData }) => {
  const tableData = {
    columns: [
      // Workers are still identified by their legacy id (old_id); fall back to
      // the internal worker_id only when a worker has no legacy id.
      { key: 'workerId', label: 'Worker ID', cell: (value, row) => row.oldId || row.workerId },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'lastActivity', label: 'Last Activity' },
      { key: 'timePassed', label: 'Time Passed' },
      { key: 'blockName', label: 'Block Name' },
      { key: 'location', label: 'Location', cell: (value) => value ? value.split(',').slice(0, 2).join(', ') : '' }
    ],
    rows: lastActivitiesData
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const getGroupWorkers = (group, lastActivitiesData = []) => {
  if (!group) return []

  const groupWorkers = [
    group.workers,
    group.workersList,
    group.workerList,
    group.workerDetails,
    group.workersDetails,
    group.workerData,
    group.workersData,
    group.details,
    group.items,
    group.data
  ].find(value => Array.isArray(value) || Array.isArray(value?.workers) || Array.isArray(value?.items))

  let workers = lastActivitiesData.filter(worker => worker.groupName === group.groupName)
  if (Array.isArray(groupWorkers)) {
    workers = groupWorkers
  } else if (Array.isArray(groupWorkers?.workers)) {
    workers = groupWorkers.workers
  } else if (Array.isArray(groupWorkers?.items)) {
    workers = groupWorkers.items
  }

  return workers.map(worker => ({
    // Legacy id (old_id) takes priority; internal worker_id is the fallback.
    workerId: worker.oldId || worker.old_id || worker.workerId || worker.worker_id || '',
    workerName: worker.workerName || worker.worker_name || worker.name || '',
    groupName: worker.groupName || worker.group_name || group.groupName || group.group_name || '',
    activityName: worker.activityName || worker.activity_name || worker.lastActivity || worker.last_activity || '',
    lastWorkingDay: worker.lastWorkingDay || worker.last_working_day || worker.date || worker.createdAt || worker.created_at || '',
    startTime: worker.startTime || worker.start_time || ''
  }))
}

const getBlockWorkers = (block, lastActivitiesData = []) => {
  if (!block) return []

  const blockWorkers = [
    block.workers,
    block.workersList,
    block.workerList,
    block.workerDetails,
    block.workersDetails,
    block.workerData,
    block.workersData,
    block.details,
    block.items,
    block.data
  ].find(value => Array.isArray(value) || Array.isArray(value?.workers) || Array.isArray(value?.items))

  let workers = lastActivitiesData.filter(worker => worker.blockName === block.blockName)
  if (Array.isArray(blockWorkers)) {
    workers = blockWorkers
  } else if (Array.isArray(blockWorkers?.workers)) {
    workers = blockWorkers.workers
  } else if (Array.isArray(blockWorkers?.items)) {
    workers = blockWorkers.items
  }

  return workers.map(worker => ({
    // Legacy id (old_id) takes priority; internal worker_id is the fallback.
    workerId: worker.oldId || worker.old_id || worker.workerId || worker.worker_id || '',
    workerName: worker.workerName || worker.worker_name || worker.name || '',
    blockName: worker.blockName || worker.block_name || block.blockName || block.block_name || '',
    activityName: worker.activityName || worker.activity_name || worker.lastActivity || worker.last_activity || '',
    deviceGroupName: worker.deviceGroupName || worker.device_group_name || worker.groupName || worker.group_name || '',
    eventTime: worker.eventTime || worker.event_time || worker.createdAt || worker.created_at || worker.time || ''
  }))
}

const WorkersByGroupDetailsTable = ({ group, lastActivitiesData }) => {
  const tableData = {
    columns: [
      { key: 'workerId', label: 'Worker ID' },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'lastWorkingDay', label: 'Last Working Day' },
      { key: 'startTime', label: 'Start Time' }
    ],
    rows: getGroupWorkers(group, lastActivitiesData)
  }

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersInBlockDetailsTable = ({ block, lastActivitiesData }) => {
  const tableData = {
    columns: [
      { key: 'workerId', label: 'Worker ID' },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'blockName', label: 'Block Name' },
      { key: 'deviceGroupName', label: 'Device Group Name' },
      { key: 'eventTime', label: 'Event Time' }
    ],
    rows: getBlockWorkers(block, lastActivitiesData)
  }

  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const LastDayWorkingSummary = () => {
  const { data, updateDateRange } = useWorkdaySummary()
  const filterOptions = useDashboardFilterOptions(data.farmId)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedBlock, setSelectedBlock] = useState(null)
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)
  const dateRange = data.dateRange || createDefaultDateRange(data.filterDate)

  const closeGroupDetailsModal = () => {
    setSelectedGroup(null)
  }

  const closeBlockDetailsModal = () => {
    setSelectedBlock(null)
  }

  if (data.loading) {
    return <PageLoadingState message='Loading last working day summary...' rows={12} />
  }

  return (
    <div className='p-6 bg-white-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-600'>Last Working Day Summary</h1>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>Advanced filters</span>
          </ButtonComponent>
        </div>
      </div>
      <hr className='my-8 border-gray-300' />
      <div className='flex gap-6'>
        <section className='w-1/2'>
          <h1 className='text-2xl text-gray-500 mb-4'>Workers By Group</h1>
          <p className='text-gray-500'>Working Range ({getDateRangeSummary(dateRange)})</p>
          {data.loading
            ? (
              <div className='w-full h-96 min-h-[600px] p-6 flex items-center justify-center'>
                <div className='text-gray-500'>Loading chart data...</div>
              </div>
              )
            : (
              <WorkersByGroupChart
                data={data.workersByGroup}
                totalWorkers={data.totalWorkers}
                onGroupClick={setSelectedGroup}
              />
              )}
        </section>
        <section className='w-1/2'>
          <h1 className='text-2xl text-gray-500 mb-4'>Workers In Block</h1>
          <p className='text-gray-500'>Working Range ({getDateRangeSummary(dateRange)})</p>
          {data.loading
            ? (
              <div className='w-full h-96 min-h-[600px] p-6 flex items-center justify-center'>
                <div className='text-gray-500'>Loading chart data...</div>
              </div>
              )
            : (
              <WorkersInBlockChart data={data.workersInBlock} onBlockClick={setSelectedBlock} />
              )}
        </section>
      </div>
      <section className='mt-12'>
        <h1 className='text-2xl text-gray-500 mb-4'>Farm Activity Summary</h1>
        {data.loading
          ? (
            <div className='text-gray-500'>Loading farm activity data...</div>
            )
          : data.error
            ? (
              <div className='text-red-500'>{data.error}</div>
              )
            : (
              <FarmActivitySummaryTable farmActivityData={data.farmActivityData} />
              )}
      </section>
      <section className='mt-12'>
        <h1 className='text-2xl text-gray-500 mb-4'>Last Activities Board</h1>
        {data.loading
          ? (
            <div className='text-gray-500'>Loading last activities data...</div>
            )
          : data.error
            ? (
              <div className='text-red-500'>{data.error}</div>
              )
            : (
              <LastActivitiesBoardTable lastActivitiesData={data.lastActivitiesData} />
              )}
      </section>
      <ModalComponent
        isOpen={Boolean(selectedGroup)}
        onClose={closeGroupDetailsModal}
        title={`Workers in ${selectedGroup?.groupName || 'Group'}`}
        maxWidth='5xl'
        customFooter={(
          <button
            type='button'
            onClick={closeGroupDetailsModal}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
          >
            Close
          </button>
        )}
      >
        <WorkersByGroupDetailsTable group={selectedGroup} lastActivitiesData={data.lastActivitiesData} />
      </ModalComponent>
      <ModalComponent
        isOpen={Boolean(selectedBlock)}
        onClose={closeBlockDetailsModal}
        title={`Workers in ${selectedBlock?.blockName || 'Block'}`}
        maxWidth='6xl'
        customFooter={(
          <button
            type='button'
            onClick={closeBlockDetailsModal}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200'
          >
            Close
          </button>
        )}
      >
        <WorkersInBlockDetailsTable block={selectedBlock} lastActivitiesData={data.lastActivitiesData} />
      </ModalComponent>
      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={data.currentDate}
        onClose={() => setShowDateRangeModal(false)}
        onApply={(nextRange) => {
          updateDateRange(nextRange)
          setShowDateRangeModal(false)
        }}
      />
    </div>
  )
}

export default LastDayWorkingSummary
